import { dayGross, orlofDayRate, orlofPctFor, rates } from './engine.js';
import { iso, parseDate, periodInfo, periodKey } from './period.js';
import type {
  CompanySettings,
  Employee,
  OrlofEntry,
  OrlofEntryDraft,
  OrlofStatus,
} from './types.js';

/**
 * Orlof — the Icelandic vacation account.
 *
 * What makes this different from the rest of the engine: orlof is not a
 * property of a pay period, it is a *balance* that runs across them. Money
 * accrues on every krona of wages, sits in an account, and comes out again
 * when the employee takes vacation or leaves. So a period's own accrual is
 * only ever one line of a statement, never the statement itself.
 *
 * Two rules drive everything here:
 *
 *  - **The orlofsár runs 1 May – 30 April.** Accrual earned in one runs out
 *    over the next. It is not the calendar year and it is not the pay period.
 *  - **What came before carries forward.** A balance is the opening balance
 *    plus everything since, so it can only fall when somebody takes vacation
 *    or is paid out — never because the screen moved to a later month.
 *
 * The ledger (`OrlofEntry[]`) is the record of what has been settled. Anything
 * after the last stored entry is computed live from the worklog, so the figure
 * is right whether or not a period has been posted yet. Posted rows carry the
 * day rate they were valued at, which is what stops a pay rise from silently
 * re-pricing vacation somebody earned two years ago.
 */

/** The orlofsár opens on 1 May. */
export const ORLOF_YEAR_START_MONTH = 5;

/** The orlofsár a date falls in, named for the calendar year its 1 May starts. */
export function orlofYearOf(date: string): number {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  return month >= ORLOF_YEAR_START_MONTH ? year : year - 1;
}

export function orlofYearRange(year: number): { start: string; end: string } {
  return { start: `${year}-05-01`, end: `${year + 1}-04-30` };
}

/** How an orlofsár reads to a human: `2026/27`. */
export function orlofYearLabel(year: number): string {
  return `${year}/${String((year + 1) % 100).padStart(2, '0')}`;
}

function addDays(date: string, n: number): string {
  const d = parseDate(date);
  d.setDate(d.getDate() + n);
  return iso(d);
}

/** Accrual earned over a date range, straight from the worklog. */
export function accrueRange(
  emp: Pick<Employee, 'monthlyWage' | 'agreementId' | 'startDate' | 'worklog'>,
  settings: CompanySettings,
  from: string,
  to: string,
): { isk: number; days: number; basisIsk: number; pct: number; dayRate: number } {
  const dayRate = orlofDayRate(emp, settings);
  const pct = orlofPctFor(emp, settings, to);
  if (from > to) return { isk: 0, days: 0, basisIsk: 0, pct, dayRate };

  const r = rates(settings, emp);
  let isk = 0;
  let basisIsk = 0;

  for (const d = parseDate(from); iso(d) <= to; d.setDate(d.getDate() + 1)) {
    const ds = iso(d);
    const w = emp.worklog[ds];
    if (!w) continue;
    const dow = d.getDay();
    const { gross } = dayGross(
      {
        h: w.h || 0,
        sick: !!w.sick,
        vacation: !!w.vacation,
        weekend: dow === 0 || dow === 6,
        holiday: settings.holidays.includes(ds),
      },
      r,
      settings,
    );
    if (gross <= 0) continue;
    basisIsk += gross;
    isk += (gross * orlofPctFor(emp, settings, ds)) / 100;
  }

  return { isk, days: dayRate > 0 ? isk / dayRate : 0, basisIsk, pct, dayRate };
}

/** Vacation days recorded in the worklog over a date range. */
export function vacationDaysIn(
  emp: Pick<Employee, 'worklog'>,
  from: string,
  to: string,
): number {
  if (from > to) return 0;
  let n = 0;
  for (const [date, w] of Object.entries(emp.worklog)) {
    if (w.vacation && date >= from && date <= to) n++;
  }
  return n;
}

/**
 * The last date the ledger *covers*, as opposed to the last date it mentions.
 *
 * Only two kinds of row close off a stretch of time: an `opening`, which
 * subsumes everything before it, and a posted period, which is any row
 * carrying a `periodKey`. A payout or a hand-written correction is a point
 * event — it says nothing about whether the weeks around it have been settled,
 * and letting one move the mark would silently erase the live accrual behind it.
 */
export function coverageThrough(entries: OrlofEntry[], asOf?: string): string | null {
  let mark: string | null = null;
  for (const e of entries) {
    if (asOf && e.date > asOf) continue;
    if (e.kind !== 'opening' && !e.periodKey) continue;
    if (!mark || e.date > mark) mark = e.date;
  }
  return mark;
}

export interface OrlofStatusInput {
  emp: Pick<Employee, 'monthlyWage' | 'agreementId' | 'startDate' | 'worklog'>;
  settings: CompanySettings;
  /** The employee's ledger. Order does not matter. */
  entries: OrlofEntry[];
  /** Statement date — normally the end of the period on screen. */
  asOf: string;
}

/**
 * The vacation account as at one date.
 *
 * Stored entries are authoritative up to the last one dated on or before
 * `asOf`; the remainder is accrued live from the worklog. Asking for a date in
 * the past therefore gives the balance *as it stood then*, not today's balance
 * with one month subtracted — which was the bug this function exists to end.
 */
export function orlofStatus({ emp, settings, entries, asOf }: OrlofStatusInput): OrlofStatus {
  const year = orlofYearOf(asOf);
  const { start, end } = orlofYearRange(year);
  const dayRate = orlofDayRate(emp, settings);
  const pct = orlofPctFor(emp, settings, asOf);

  // The opening row is the floor of what the ledger knows, so it counts even
  // when the statement date is behind it. Asking for a period before the
  // account was handed over then shows the handover balance and no accrual,
  // which is the honest answer: there is no history here to reconstruct.
  const opening = entries.find((e) => e.kind === 'opening') ?? null;
  const upTo = entries
    .filter((e) => e.date <= asOf || e === opening)
    .sort((a, b) => a.date.localeCompare(b.date));
  const postedThrough = coverageThrough(upTo, asOf);

  // Everything on or before an `opening` row *is* that row, by definition, so
  // it is never re-derived from the worklog. With no ledger at all, fall back
  // to the whole employment — a fresh install still shows a real carry-forward.
  const floor = opening ? addDays(opening.date, 1) : emp.startDate;
  const liveFrom = max(floor, postedThrough ? addDays(postedThrough, 1) : floor);

  // The live tail can straddle 1 May, so split it where the orlofsár does.
  const priorTail = accrueRange(emp, settings, liveFrom, min(asOf, addDays(start, -1)));
  const yearTail = accrueRange(emp, settings, max(liveFrom, start), asOf);

  let openingIsk = priorTail.isk;
  let openingDays = priorTail.days;
  let accruedIsk = yearTail.isk;
  let accruedDays = yearTail.days;
  let takenIsk = 0;
  let takenDays = 0;
  let paidIsk = 0;
  let paidDays = 0;
  let adjustIsk = 0;
  let adjustDays = 0;

  for (const e of upTo) {
    if (e.date < start) {
      // Anything settled in an earlier orlofsár is simply what was carried in.
      openingIsk += e.isk;
      openingDays += e.days;
      continue;
    }
    switch (e.kind) {
      case 'accrual':
        accruedIsk += e.isk;
        accruedDays += e.days;
        break;
      case 'taken':
        takenIsk -= e.isk;
        takenDays -= e.days;
        break;
      case 'payout':
        paidIsk -= e.isk;
        paidDays -= e.days;
        break;
      // An opening row dated inside the orlofsár is somebody joining mid-year.
      case 'opening':
        openingIsk += e.isk;
        openingDays += e.days;
        break;
      default:
        adjustIsk += e.isk;
        adjustDays += e.days;
    }
  }

  // Vacation taken since the last posting is in the worklog, not the ledger.
  const liveTakenFrom = max(liveFrom, start);
  const liveTakenDays = vacationDaysIn(emp, liveTakenFrom, asOf);
  takenDays += liveTakenDays;
  takenIsk += liveTakenDays * dayRate;

  return {
    year,
    label: orlofYearLabel(year),
    start,
    end,
    asOf,
    openingIsk,
    openingDays,
    accruedIsk,
    accruedDays,
    takenIsk,
    takenDays,
    paidIsk,
    paidDays,
    adjustIsk,
    adjustDays,
    balanceIsk: openingIsk + accruedIsk + adjustIsk - takenIsk - paidIsk,
    balanceDays: openingDays + accruedDays + adjustDays - takenDays - paidDays,
    dayRate,
    pct,
    postedThrough,
    liveIsk: priorTail.isk + yearTail.isk,
    liveDays: priorTail.days + yearTail.days,
  };
}

function min(a: string, b: string): string {
  return a < b ? a : b;
}

function max(a: string, b: string): string {
  return a > b ? a : b;
}

/** How far back posting will reach on a ledger that has never been written. */
const MAX_PERIODS_TO_POST = 120;

/**
 * Accrual and vacation entries for every pay period that has closed but not
 * yet been posted.
 *
 * Posting is what turns a live calculation into a record: the ISK, the rate
 * and the day value are frozen at the period's own numbers, so the account
 * survives a pay rise, a seniority step and the worklog eventually ageing out.
 * Callers write these under a uniqueness constraint on (employee, period), so
 * running it twice is harmless.
 */
export function pendingAccruals(
  emp: Pick<Employee, 'monthlyWage' | 'agreementId' | 'startDate' | 'worklog'>,
  settings: CompanySettings,
  entries: OrlofEntry[],
  today: string,
): OrlofEntryDraft[] {
  const covered = coverageThrough(entries);
  const from = covered ? addDays(covered, 1) : emp.startDate;
  if (from > today) return [];

  // Walk back from the current period until we reach one already covered.
  const closed: Array<{ start: string; end: string; key: string }> = [];
  for (let offset = -1; offset >= -MAX_PERIODS_TO_POST; offset--) {
    const p = periodInfo(settings, today, offset);
    if (p.end < from) break;
    closed.push({ start: p.start, end: p.end, key: periodKey(p) });
  }
  closed.reverse();

  const drafts: OrlofEntryDraft[] = [];
  closed.forEach((p, i) => {
    // The earliest row starts where the ledger left off, which covers two
    // cases: a period already half-posted, and a backlog longer than the walk
    // above was willing to iterate. The second matters — posting advances the
    // coverage mark to the newest row, so anything beyond the cap would be
    // left both unposted and behind the mark, and would vanish from the
    // balance. Correctness must not depend on how far back the walk reached.
    const windowFrom = i === 0 ? from : p.start;
    const a = accrueRange(emp, settings, windowFrom, p.end);
    /*
     * A period that accrued nothing is left unposted.
     *
     * This used to write a zero row so the coverage mark would advance past
     * periods nobody worked. That gave a row asserting nothing the power to
     * silence a real calculation: post a zero for July because the worklog had
     * not been imported yet, and July is marked settled forever — the hours
     * arrive the next day and the balance never moves, because the live tail
     * starts after the mark.
     *
     * There is nothing to protect in an empty accrual. It locks in no rate and
     * no value, and re-deriving it from the worklog costs a walk over a few
     * dates. So the mark only advances over a period that actually earned
     * something, and a period with no data stays open to being computed again.
     */
    if (a.isk === 0 && a.days === 0) return;
    drafts.push({
      orlofYear: orlofYearOf(p.end),
      date: p.end,
      kind: 'accrual',
      periodKey: p.key,
      isk: a.isk,
      days: a.days,
      basisIsk: a.basisIsk,
      pct: a.pct,
      dayRate: a.dayRate,
      note: '',
    });
    const taken = vacationDaysIn(emp, windowFrom, p.end);
    if (taken > 0) {
      drafts.push({
        orlofYear: orlofYearOf(p.end),
        date: p.end,
        kind: 'taken',
        periodKey: p.key,
        isk: -taken * a.dayRate,
        days: -taken,
        basisIsk: 0,
        pct: 0,
        dayRate: a.dayRate,
        note: '',
      });
    }
  });
  return drafts;
}
